/**
 * Funder profile — the full picture behind one foundation lead.
 *
 * The digest card and the dashboard row are space-constrained: they excerpt the
 * application text and show at most a few past grants. This page is where the
 * rest lives — the complete Part XV application details and the funder's filed
 * giving history, which is the evidence a grant writer actually qualifies a
 * prospect on.
 *
 * Everything here is public IRS 990 data, restated. Nothing is inferred: if the
 * filing left a field blank, the row is absent rather than guessed at.
 */

import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getFunderProfile, recordFunderFeedback } from '../api'
import { getClientId } from '../session'
import { useDocumentTitle } from '../components/PublicLayout'
import EinPrompt from '../components/EinPrompt'

function Row({ label, children }) {
  if (!children) return null
  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-700 whitespace-pre-line">{children}</dd>
    </div>
  )
}

function Stat({ label, value }) {
  if (!value && value !== 0) return null
  return (
    <div className="rounded-lg bg-gray-50 px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-gray-900">{value}</div>
    </div>
  )
}

/**
 * Client-relative evidence, rendered in its own block and never interleaved
 * with the foundation's own facts above.
 *
 * This is the display half of the separation the API makes in `your_fit`: a
 * reader must never be able to mistake "your organization" for "this
 * foundation". The 2026-07-27 blurb incident put a client's own city inside a
 * funder description twice on a live account, because one flat block of mixed
 * facts has no notion of whose fact is whose.
 *
 * Every claim degrades to silence rather than to a guess: when we hold no EIN
 * for the org we say we cannot check, and when we have no state or focus areas
 * the peer count is omitted entirely.
 */
function YourFit({ fit, onEinSaved }) {
  if (!fit) return null

  const showFunded = fit.already_funded_known
  const showPeers = fit.peer_count_known && fit.peer_count > 0

  // `already_funded_known` is false for exactly one reason: no usable EIN on
  // file (`peer_evidence.load_already_funded`). So this block is a second place
  // where a missing EIN costs the reader a specific, visible answer — and
  // therefore a place worth asking for it (ADR-0012's revisit note). Before
  // this, the section simply vanished and the client was never told that a
  // question they would obviously want answered had gone unasked.
  if (!showFunded && !showPeers) {
    return (
      <section className="mt-8 rounded-lg border border-dashed border-gray-300 px-4 py-4 text-center">
        <h2 className="text-sm font-semibold text-gray-900">
          Have they funded you before?
        </h2>
        <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
          We can check this foundation’s filings for a grant to your
          organization, but only against your EIN, and we don’t have one on
          file. A renewal is a very different conversation than a first ask.
        </p>
        <EinPrompt onSaved={onEinSaved} cta="Check" />
      </section>
    )
  }

  return (
    <section className="mt-8 rounded-lg border border-brand-100 bg-brand-50/40 px-4 py-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
        How they relate to your organization
      </h2>
      <ul className="mt-3 space-y-2 text-sm text-gray-700">
        {showPeers && (
          <li>
            <span className="font-semibold text-gray-900">
              Supports {fit.peer_count}{' '}
              {fit.peer_count === 1 ? 'organization' : 'organizations'} like yours
            </span>
            <span className="text-gray-600">
              {': '}grantees in {fit.peer_state} whose funded work overlaps your
              focus areas
              {fit.peer_scale_bounded && ', at grant sizes in your range'}
              {fit.peer_since_year && `, since ${fit.peer_since_year}`}.
            </span>
          </li>
        )}
        {showFunded && fit.already_funded && (
          <li>
            <span className="font-semibold text-gray-900">
              They have funded you before
            </span>
            <span className="text-gray-600">
              {': '}{fit.already_funded_grants}{' '}
              {fit.already_funded_grants === 1 ? 'grant' : 'grants'} on file
              {fit.already_funded_total && `, ${fit.already_funded_total} total`}
              {fit.already_funded_latest_year &&
                `, most recently in ${fit.already_funded_latest_year}`}
              . A renewal is a different conversation than a first ask.
            </span>
          </li>
        )}
        {showFunded && !fit.already_funded && (
          <li>
            <span className="font-semibold text-gray-900">
              No filed grant to your organization
            </span>
            <span className="text-gray-600">
              {': '}nothing in their 990s names your EIN, so this would be a new
              relationship.
            </span>
          </li>
        )}
      </ul>
      <p className="mt-3 text-xs text-gray-500">
        Based on grants these filings itemize. Recipient EINs are missing from
        many 990 rows, so counts are a floor rather than an exact tally.
      </p>
    </section>
  )
}

export default function FunderProfile() {
  useDocumentTitle('Foundation profile')
  const { funderId } = useParams()
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [feedback, setFeedback] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getFunderProfile(funderId)
      .then(data => {
        if (cancelled) return
        setProfile(data)
        setFeedback(data.feedback)
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Could not load this foundation.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [funderId])

  async function handleFeedback(value) {
    setFeedback(value)
    try {
      await recordFunderFeedback({
        client_id: getClientId(),
        funder_id: funderId,
        feedback: value,
      })
    } catch {
      setFeedback(null) // let them try again rather than lie about it
    }
  }

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-gray-500">Loading…</div>
  }

  // A 404 here is the normal shape of "not one of your leads", not a bug.
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-xl font-semibold text-gray-900">Foundation not found</h1>
        <p className="mt-2 text-sm text-gray-600">
          This foundation isn’t one of your current leads. It may have been
          filtered out since your last digest, or the link may be out of date.
        </p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm font-medium text-brand-500 hover:underline">
          ← Back to your dashboard
        </Link>
      </div>
    )
  }

  const p = profile
  const location = [p.city, p.state].filter(Boolean).join(', ')

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to your leads
      </Link>

      <header className="mt-4">
        <h1 className="text-2xl font-bold text-gray-900">{p.name}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {location}
          {p.funder_type && <> · {p.funder_type}</>}
          {p.filing_year && <> · IRS filing {p.filing_year}</>}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
          {p.website && (
            <a href={p.website} target="_blank" rel="noreferrer"
               className="font-medium text-brand-500 hover:underline">
              {p.website_display} →
            </a>
          )}
          {p.ein_url && (
            <a href={p.ein_url} target="_blank" rel="noreferrer"
               className="text-gray-500 hover:underline">
              EIN {p.ein}, see their 990s →
            </a>
          )}
        </div>
      </header>

      {p.why_matched && (
        <p className="mt-5 border-l-2 border-brand-100 pl-3 text-sm italic text-gray-600">
          {p.why_matched}
        </p>
      )}

      {/* A funder reached from corpus search rather than from the client's own
          leads. Pro can open any of them (ADR-0012), so the page has to say
          which kind it is — otherwise a foundation the matcher deliberately
          passed over looks identical to one it recommended. Rendered in the
          server's words: the phrasing rule (a 990 records where a funder HAS
          given, never where it would refuse) is enforced there, with tests. */}
      {p.why_not && !p.why_not.matched && (
        <p className="mt-5 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600">
          {p.why_not.explanation}
        </p>
      )}

      {p.verify_note && (
        <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          ⚠️ {p.verify_note}
        </p>
      )}

      <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Typical grant" value={p.typical_grant} />
        <Stat label="Median grant" value={p.median_grant} />
        <Stat label="Paid last year" value={p.grants_paid_total} />
        <Stat label="Grants last year" value={p.grants_paid_count} />
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
          How to approach them
        </h2>
        <dl className="mt-2">
          {/* Restrictions first — the fastest way to rule a funder out. */}
          <Row label="Restrictions">{p.restrictions}</Row>
          {/* The filer said "none"/"no submission deadlines". That placeholder is
              stripped before display, so without this the row disappeared and a
              funder open all year looked the same as one that told us nothing. */}
          {!p.application_deadlines && p.deadlines_year_round && (
            <Row label="Deadlines">Accepts applications year-round</Row>
          )}
          <Row label={p.deadlines_stale ? 'Past deadline (as filed)' : 'Deadlines'}>
            {p.application_deadlines && (
              <>
                {p.application_deadlines}
                {p.deadlines_stale && (
                  <span className="block text-xs text-amber-700 mt-1">
                    This cycle has closed. Most foundations repeat it annually, so
                    confirm the current dates before applying.
                  </span>
                )}
              </>
            )}
          </Row>
          <Row label="How to apply">{p.application_info}</Row>
          <Row label="Contact">{p.application_contact}</Row>
          <Row label="States they fund">
            {p.recipient_states?.length ? p.recipient_states.join(', ') : null}
          </Row>
        </dl>
      </section>

      {/* Re-fetch rather than patch locally: `your_fit` is assembled
          server-side from the stored EIN, so the server's answer is the only
          one worth showing. Silent on failure — the form already reported any
          save error, and a second one here would be noise. */}
      <YourFit
        fit={p.your_fit}
        onEinSaved={() =>
          getFunderProfile(funderId).then(setProfile).catch(() => {})
        }
      />

      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
          What they actually funded
        </h2>
        {p.grants.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">
            Their latest filing doesn’t itemize individual grants.
          </p>
        ) : (
          <>
            {/*
              Ordered by relevance to this client (latest filing, own state,
              overlapping focus areas, comparable size), matching the digest
              card — not by amount. The copy has to say so, or a reader sorting
              for the biggest cheque will think the list is broken.
            */}
            <p className="mt-1 text-xs text-gray-500">
              {p.grants_available > p.grants_shown
                ? `${p.grants_shown} of ${p.grants_available} grants in their latest filings, closest to your work first.`
                : `Every grant itemized in their latest filings, closest to your work first.`}
            </p>
            <ul className="mt-3 divide-y divide-gray-100">
              {p.grants.map((g, i) => (
                <li key={i} className="flex items-baseline justify-between gap-4 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {g.recipient}
                      {(g.recipient_city || g.recipient_state) && (
                        <span className="font-normal text-gray-500">
                          {' '}· {[g.recipient_city, g.recipient_state].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                    {g.purpose && <div className="truncate text-xs text-gray-500">{g.purpose}</div>}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {/* No amount filed stays blank — never rendered as $0. */}
                    {g.amount && <div className="text-sm font-semibold text-gray-900">{g.amount}</div>}
                    <div className="text-xs text-gray-500">{g.year}</div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* Feedback needs a FunderMatch row — POST /api/funder-feedback 404s
          without one. A corpus funder the client has no match for would offer
          thumbs that silently fail, so the whole section is withheld rather
          than rendered into a dead end.
          ⚠️ Conditional render, NOT the `hidden` attribute: this section is
          `flex`, and display:flex beats the UA stylesheet's display:none, so
          `hidden` here would do nothing at all. */}
      {(p.why_not?.matched ?? true) && (
      <section className="mt-8 flex items-center gap-3 border-t border-gray-100 pt-4">
        {feedback ? (
          <span className="text-xs text-gray-500">
            {feedback === 'relevant' ? '👍 Marked relevant' : '👎 Marked not for us'}
          </span>
        ) : (
          <>
            <span className="text-sm text-gray-500">Is this a good lead?</span>
            <button onClick={() => handleFeedback('relevant')}
                    className="text-sm text-gray-500 transition-colors hover:text-green-600">👍 Relevant</button>
            <button onClick={() => handleFeedback('irrelevant')}
                    className="text-sm text-gray-500 transition-colors hover:text-red-500">👎 Not for us</button>
          </>
        )}
      </section>
      )}

      <p className="mt-6 text-xs text-gray-500">
        All figures come from this foundation’s own IRS Form 990 filings
        {p.filing_year ? ` (most recent: ${p.filing_year})` : ''}. A foundation’s
        priorities can change between filings, so confirm before you apply.
      </p>
    </div>
  )
}
